import { getUID } from '@redviking/argonaut-util/src/getUID';
import type { NodeCfg } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { ArgoConnectionKindEnum } from '@redviking/argonaut-util/types/db-enums/argo/connection_kind';
import { defineControlOpts } from '../../controls/FlowNodeControl';
import type { NodeSpec } from './NodeSpec';
import { getISDUVariableOptions } from './isdu-variables';

export const isduReadNodeSpec: NodeSpec<NodeCfg<'isduRead'>> = {
    nodeCategory: 'connection',
    nodeSubCategory: 'IO Link',
    requiredSockets: 'none',
    kindLabel: 'ISDU Read',
    controls: nodeCfg => ({
        connectionId: defineControlOpts({
            cfg: {
                kind: 'connectionSelect',
                label: 'Connection',
                allowedConnectionKinds: [ ArgoConnectionKindEnum.IoLinkMaster ],
            },
            on: {
                // the available variables come from the iodd of the device on the selected connection, so a different connection invalidates them
                'update:value': (connectionId: string, { flowEventEmitter }) => flowEventEmitter.emit('updateNodeCfg', {
                    newNodeCfg: {
                        ...nodeCfg,
                        controlValues: {
                            ...nodeCfg.controlValues,
                            connectionId,
                            isduVariables: connectionId === nodeCfg.controlValues.connectionId ? nodeCfg.controlValues.isduVariables : {},
                        },
                    },
                }),
            },
            value: nodeCfg.controlValues.connectionId,
        }),
        channelNumber: defineControlOpts({
            cfg: { kind: 'number', label: 'Channel', min: 0, max: 4 },
            on: {
                'update:value': (channelNumber: number, { flowEventEmitter }) => flowEventEmitter.emit('updateNodeCfg', {
                    newNodeCfg: {
                        ...nodeCfg,
                        controlValues: {
                            ...nodeCfg.controlValues,
                            channelNumber,
                            isduVariables: channelNumber === nodeCfg.controlValues.channelNumber ? nodeCfg.controlValues.isduVariables : {},
                        },
                    },
                }),
            },
            value: nodeCfg.controlValues.channelNumber,
        }),
        isduVariables: defineControlOpts({
            cfg: {
                kind: 'selectWithCallback',
                selectLabel: 'Add ISDU Variable',
                items: async () => {
                    const available = await getISDUVariableOptions({
                        connectionId: nodeCfg.controlValues.connectionId,
                        channelNumber: nodeCfg.controlValues.channelNumber,
                        access: 'r',
                    });
                    return Object.entries(available)
                        .filter(([ id ]) => !nodeCfg.controlValues.isduVariables[id]?.tracked)
                        .map(([ id, details ]) => ({ value: id, label: details.name }));
                },
            },
            on: {
                'update:value': () => {}, // no-op, this control does not use the update:value event
                'select': async (value, { flowEventEmitter }) => {
                    const available = await getISDUVariableOptions({
                        connectionId: nodeCfg.controlValues.connectionId,
                        channelNumber: nodeCfg.controlValues.channelNumber,
                        access: 'r',
                    });
                    const selected = available[value as string];
                    if (!selected) {
                        return;
                    }
                    flowEventEmitter.emit('updateNodeCfg', {
                        newNodeCfg: {
                            ...nodeCfg,
                            controlValues: {
                                ...nodeCfg.controlValues,
                                isduVariables: {
                                    ...nodeCfg.controlValues.isduVariables,
                                    [value as string]: { ...selected, tracked: true },
                                },
                            },
                        },
                    });
                },
            },
            value: nodeCfg.controlValues.isduVariables,
        }),
    }),
    cfg: () => ({ id: getUID(), kind: 'isduRead', width: 220, controlValues: { connectionId: '', channelNumber: -1, isduVariables: {} } }),
    inputs: () => ({ trigger: { socket: 'event', label: 'Request ISDU Read' } }),
    outputs: nodeCfg => {
        const outputs: ReturnType<NodeSpec<NodeCfg<'isduRead'>>['outputs']> = {
            response: { socket: 'event', label: 'ISDU Read Response' },
        };
        for (const [ id, value ] of Object.entries(nodeCfg.controlValues.isduVariables)) {
            if (value.tracked) {
                outputs[`output_${id}`] = {
                    socket: value.dataKind,
                    label: value.name,
                    controlOpts: defineControlOpts({
                        cfg: { kind: 'deletableSocket' },
                        value: null,
                        on: {
                            'update:value': () => {}, // no-op, this control does not use the update:value event
                            'delete': (_, { flowEventEmitter }) => flowEventEmitter.emit('removeNodeSocket', {
                                newNodeCfg: {
                                    ...nodeCfg,
                                    controlValues: {
                                        ...nodeCfg.controlValues,
                                        isduVariables: {
                                            ...nodeCfg.controlValues.isduVariables,
                                            [id]: { ...value, tracked: false },
                                        },
                                    },
                                },
                                direction: 'output',
                                removalMethod: { kind: 'uniqueIdentifier', value: `output_${id}` },
                            }),
                        },
                    }),
                };
            }
        }
        return outputs;
    },
    validator: node => node.controlValues.connectionId ? true : { connectionId: 'Connection id is required' },
};
